"use client";

import { dataLayer } from "@repo/analytics/data-layer-utils";
import {
  componentNameConstants,
  dlElementConstants,
  dlEventConstants,
} from "@repo/analytics/data-layer.constants";
import BFLSkeleton from "@repo/shared-ui/BFLSkeleton";
import {
  selectIsAuthPending,
  selectIsAuthSuccessOrPerpetual,
  useAppSelector,
} from "@repo/web-store";
import { usePathname, useSearchParams } from "next/navigation";
import { ComponentProps, FC, Suspense, useEffect, useMemo, useState } from "react";
import { twMerge } from "tailwind-merge";
import { updateCleverTapPageObject } from "@repo/sso/utils";
import { IResponse } from "@repo/sso/api";
import { getHoldings } from "../../services/TradingMallService";
import PortfolioCardRender from "./PortfolioCardRender";
import { CmpDataPortfolioProps, HoldingsResponse } from "./PortfolioTypes";
import Report from "./Reports";
import Conditional from "@repo/shared-ui/Conditional";

interface PortfolioContainerProps {
  cmpData: CmpDataPortfolioProps;
  className?: string;
}

type TabType = "portfolio" | "report";

const PortfolioContainer: FC<PortfolioContainerProps> = ({ cmpData, className }) => {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const [activeTab, setActiveTab] = useState<TabType>("portfolio");
  const [holdings, setHoldings] = useState<HoldingsResponse | null>(null);

  const isAuthSuccessOrPerpetual = useAppSelector(
    selectIsAuthSuccessOrPerpetual,
  );
  const isAuthPending = useAppSelector(selectIsAuthPending);
  const code = searchParams.get("code") ?? "";

  const handleTabChange = (tab: TabType) => {
    setActiveTab(tab);
    const tabLabel =
      tab === "portfolio"
        ? cmpData?.holdingsTabHeading ?? "Holdings"
        : cmpData?.reportsTabHeading ?? "Reports";

    dataLayer(
      "",
      {
        section_title: "Portfolio",
        event: dlEventConstants.CLICK,
        element: dlEventConstants.TAB_CLICK,
        cta_text: tabLabel,
        event_type: dlEventConstants.TAB_CLICK,
      },
      {
        EP_PAGE_NAME: componentNameConstants.PORTFOLIO_NAME,
        EP_JOURNEY_NAME: dlElementConstants.TRADING_MALL_CLP_EP,
        EP_TAB: "null",
        EP_SUB_TAB: tabLabel,
      },
    );
  };

  // Fetch holdings data
  useEffect(() => {
    if (!code) {
      return;
    }
    (async () => {
      try {
        const response = await getHoldings(code, 1, 5) as IResponse<HoldingsResponse> | null;
        if (response?.data) {
          setHoldings(response.data);
        }
      } catch (error) {
        console.error("[PortfolioContainer] Failed to fetch holdings:", error);
      }
    })();
  }, [searchParams, code]);

  // Check if user has portfolio (stocks)
  const hasPortfolio = useMemo(
    () => {
      const result = (holdings?.holdings?.length ?? 0) > 0;
      return result;
    },
    [holdings],
  );

  // Only show tabs on portfolio-dashboard page AND if logged in AND has portfolio
  const showTabs = useMemo(() => {
    const result = (
      hasPortfolio &&
      pathname === "/portfolio-dashboard" &&
      isAuthSuccessOrPerpetual
    );
    return result;
  }, [pathname, isAuthSuccessOrPerpetual, hasPortfolio]);

  // If tabs are not shown, always render portfolio content (original behavior)
  const shouldShowPortfolio = !showTabs || activeTab === "portfolio";

  // Update CleverTap Page Object when holdings data or active tab changes
  useEffect(() => {
    if (!holdings?.portfolioSummary) return;

    const currentTab =
      activeTab === "portfolio"
        ? cmpData?.holdingsTabHeading ?? "Holdings"
        : cmpData?.reportsTabHeading ?? "Reports";

    updateCleverTapPageObject({
      EP_PAGE_NAME: componentNameConstants.PORTFOLIO_NAME,
      EP_SUB_TAB: currentTab,
      EP_CURRENT_VALUE:
        holdings.portfolioSummary.totalCurrentValue?.toString() ?? "",
      EP_INVESTED:
        holdings.portfolioSummary.totalInvestedValue?.toString() ?? "",
      EP_JOURNEY_NAME: dlElementConstants.TRADING_MALL_CLP_EP,
    });
  }, [holdings, activeTab, cmpData]);

  if (isAuthPending) {
    return (
      <BFLSkeleton count={5} height={45} width="100%" />
    );
  }

  return (
    <section className={twMerge("py-[12px] px-[16px]", className)}>

      {showTabs && (
        <div className={`flex gap-2 `}>
          <button
            type="button"
            onClick={() => handleTabChange("portfolio")}
            className={`rounded-[8px] px-[10px] py-[4px] text-[14px] font-[400px] transition-colors ${activeTab === "portfolio"
              ? "bg-[#002953] text-white"
              : "bg-[#efefef] text-[#002953]"
              }`}
          >
            {cmpData?.holdingsTabHeading ?? "Holdings"}
          </button>
          <button
            type="button"
            onClick={() => handleTabChange("report")}
            className={`rounded-[8px] px-[10px] py-[4px] text-[14px] font-[400px] transition-colors ${activeTab === "report"
              ? "bg-[#002953] text-white"
              : "bg-[#efefef] text-[#002953]"
              }`}
          >
            {cmpData?.reportsTabHeading ?? "Reports"}
          </button>
        </div>
      )}


      <Conditional if={shouldShowPortfolio}>
        <PortfolioCardRender cmpData={cmpData} />
      </Conditional>

      <Conditional if={!shouldShowPortfolio}>
        <Report cmpData={cmpData} />
      </Conditional>

    

    </section>
  );
};

// useSearchParams() requires a Suspense boundary on statically prerendered routes.
const SuspendedPortfolioContainer = (
  props: ComponentProps<typeof PortfolioContainer>,
) => (
  <Suspense fallback={<BFLSkeleton count={5} height={45} width="100%" />}>
    <PortfolioContainer {...props} />
  </Suspense>
);

export default SuspendedPortfolioContainer;
